<template>
    <Component :is="linkUrl ? NuxtLink : 'div'" v-slot="{href, navigate} = {}" :to="linkUrl" external custom>
        <div
            :class="['slice-testimonials--grid__image-card', {'slice-testimonials--grid__image-card--has-link': linkUrl}]"
            @click="linkUrl ? navigate() : undefined"
        >
            <div v-if="backgroundImage" class="slice-testimonials--grid__image-card-background" aria-hidden="true" />
            <div class="slice-testimonials--grid__image-card-overlay" aria-hidden="true" />

            <div class="slice-testimonials--grid__image-card-content">
                <PrismicImage
                    v-if="isFilled.image(item.logo)"
                    :field="item.logo"
                    :alt="item.logo.alt || ''"
                    class="slice-testimonials--grid__image-card-logo"
                    data-testid="testimonials-grid-image-card-logo"
                    width="190"
                />

                <blockquote>
                    <a v-if="linkUrl" :href="href || undefined">
                        <PrismicRichText
                            v-if="isFilled.richText(item.text)"
                            :field="item.text"
                            class="slice-testimonials--grid__image-card-quote"
                            data-testid="testimonials-grid-image-card-quote"
                        />
                    </a>
                    <PrismicRichText
                        v-else-if="isFilled.richText(item.text)"
                        :field="item.text"
                        class="slice-testimonials--grid__image-card-quote"
                        data-testid="testimonials-grid-image-card-quote"
                    />
                </blockquote>
            </div>
        </div>
    </Component>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {TestimonialsSliceTestimonialsGridPrimaryItemsItem} from '@prismicio-types';
    import {NuxtLink} from '#components';

    const props = defineProps<{
        item: TestimonialsSliceTestimonialsGridPrimaryItemsItem;
    }>();

    const linkUrl = computed(() => (isFilled.link(props.item.link) && props.item.link.url ? props.item.link.url : undefined));
    const backgroundImage = computed(() => (isFilled.image(props.item.backgroundImage) ? `url(${props.item.backgroundImage.url})` : null));
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-testimonials--grid__image-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 220px;
        overflow: hidden;
        border-radius: var(--joy-core-radius-4);
        transform-origin: center;
        will-change: transform;

        a {
            text-decoration: none;
            color: inherit;
        }

        &-background {
            position: absolute;
            inset: 0;
            background-image: v-bind(backgroundImage);
            background-position: center;
            background-repeat: no-repeat;
            background-size: cover;
            filter: blur(3px);
        }

        &-overlay {
            position: absolute;
            inset: 0;
            background: rgba(0, 0, 0, 0.7);
        }

        &-content {
            position: relative;
            z-index: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            gap: var(--joy-core-spacing-8);
            height: 100%;
            padding: var(--joy-core-spacing-9) 32px;
            color: var(--joy-color-neutral-0);
        }

        &-logo {
            width: auto;
            max-width: 190px;
            max-height: 48px;
            object-fit: contain;
            filter: brightness(0) invert(1);
        }

        &-quote {
            @include text.getFontBySize(primary-600);

            p {
                margin: 0;
                font-weight: 400;
                line-height: var(--joy-line-height-large);
            }

            :first-child {
                &::before {
                    content: '“';
                }
            }

            :last-child {
                &::after {
                    content: '”';
                }
            }
        }
    }
</style>
